import type { ReactNode } from 'react';
import type { HomePeople } from '@aithora/shared';
import type { MemberCtx } from './MemberHome';
import { chipC, D, S, Act } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// My people (design T11569-11606, hTabPeople). The care circle waits on the PROPOSED
// GET /athome/me/:id/people (the family portal's circle routes serve residents only);
// Call and Text are member actions.

const AV: Record<HomePeople['people'][number]['kind'], string> = { family: '#2F5C97', care: '#1F6B52', clinician: '#1F6B52', line: '#5B6B7C' };
const BTN_A = 'flex:2 1 110px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 16px;border-radius:13px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#2F5C97;transition:background .16s ease;';
const BTN_B = 'flex:1 1 90px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 14px;border-radius:13px;font-size:16px;font-weight:700;text-decoration:none;color:#2F5C97;border:1px solid rgba(47,92,151,.4);transition:background .16s ease;';

const initialsOf = (p: HomePeople['people'][number]) =>
  p.kind === 'line' ? p.name.charAt(0).toUpperCase() : p.name.split(' ').map((w) => w.charAt(0)).join('').slice(0, 2).toUpperCase();


/** "Daughter · Family", but "Family" once when the relation and the role say the same word. */
export const relRole = (relation: string | null | undefined, role: string | null | undefined): string => {
  const a = (relation ?? '').trim();
  const b = (role ?? '').trim();
  if (!a) return b;
  if (!b || a.toLowerCase() === b.toLowerCase()) return a;
  return a + ' · ' + b;
};

export function PeopleTab({ c, toast }: { c: MemberCtx; toast: ReactNode }) {
  const plain = useCustomerText();
  const list = c.d.people.data?.people ?? [];
  const name = c.d.me.data ? c.d.me.data.name.split(' ')[0] : '';
  const nobody = !list.some((p) => p.kind === 'family');
  const careWorker = list.some((p) => p.kind === 'care');
  return (
    <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:10px;min-width:0;align-items:start;">
      <D css="grid-column:1/-1;min-width:0;">
        <S css="display:block;font-size:21px;font-weight:700;letter-spacing:-.02em;color:#22272E;">{`${list.length}${list.length === 1 ? ' person is on your list' : ' people are on your list'}`}</S>
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.45;margin-top:3px;text-wrap:pretty;">The first one is who Aithora rings if something is wrong and you cannot answer.</S>
      </D>
      {list.map((p, pi) => {
        const first = pi === 0;
        const av = first ? 60 : 48;
        return (
          <D
            key={p.id}
            css={'grid-column:' + (first ? '1/-1' : 'auto') + ';box-sizing:border-box;border:1px solid ' + (first ? 'rgba(47,92,151,.4)' : 'rgba(34,39,46,.12)') + ';border-radius:16px;padding:' + (first ? '16px 18px' : '14px 16px') + ';min-width:0;display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:10px;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:' + pi * 55 + 'ms;'}
          >
            <S css={'display:flex;align-items:center;justify-content:center;width:' + av + 'px;height:' + av + 'px;flex:0 0 ' + av + 'px;border-radius:16px;font-size:' + (first ? 21 : 17) + 'px;font-weight:700;color:#FCFAF4;background:' + AV[p.kind] + ';'}>{initialsOf(p)}</S>
            <S css="flex:1 1 150px;min-width:0;">
              <S css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;min-width:0;">
                <S css={'display:block;font-size:' + (first ? 21 : 18) + 'px;font-weight:700;letter-spacing:-.015em;color:#22272E;'}>{p.name}</S>
                {first && <S css={chipC('#2F5C97', 'rgba(47,92,151,.14)')}>CALLED FIRST</S>}
              </S>
              <S css="display:block;font-size:16px;color:#3C454F;margin-top:2px;">
                {relRole(p.relation, p.role)}
              </S>
              <S css="display:flex;gap:14px;flex-wrap:wrap;margin-top:5px;">
                <S css="font-size:16px;color:#54606C;white-space:nowrap;">{p.lastContact}</S>
                <S css="font-size:16px;color:#54606C;white-space:nowrap;">{p.next}</S>
              </S>
            </S>
            <S css="display:flex;gap:9px;flex:1 1 190px;min-width:0;">
              <Act
                css={BTN_A}
                onClick={() =>
                  c.openSheet({
                    kicker: 'CALL',
                    title: `Call ${p.name}`,
                    glyph: '☎',
                    color: AV[p.kind],
                    body: `${relRole(p.relation, p.role)}. The call goes through the tablet speaker, so you can leave it on the table.`,
                    primary: 'Call now',
                    onPrimary: () => c.act({ kind: 'call', target: p.id }),
                  })
                }
              >
                Call
              </Act>
              <Act
                css={BTN_B}
                onClick={() =>
                  c.openSheet({
                    kicker: 'TEXT',
                    title: `Text ${p.name}`,
                    glyph: '✉',
                    color: AV[p.kind],
                    body: 'Type it or say it out loud.',
                    input: 'Your message…',
                    primary: 'Send',
                    onPrimary: (text) => c.act({ kind: 'text', target: p.id, note: text }),
                  })
                }
              >
                Text
              </Act>
            </S>
          </D>
        );
      })}
      {nobody && (
        <D css="grid-column:1/-1;min-width:0;padding-top:13px;border-top:1px solid rgba(34,39,46,.16);font-size:17px;line-height:1.5;color:#54606C;text-wrap:pretty;">
          {c.d.people.error && !c.d.people.data
            ? plain('Your people are not connected to this page yet (GET /athome/me/:id/people). The pendant and the watch still call for help.')
            : `No family contacts are on file for ${name}.${careWorker ? '' : ' No care worker is assigned yet either.'} Anyone the care team adds will appear here. There is nobody else to call from this page today.`}
        </D>
      )}
      {toast}
    </D>
  );
}
